import type { ColDef, GridApi, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Download } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../api";
import type { PortalConfig } from "../../config";
import { dateTime } from "../../format";
import { theme } from "../TicketsPage";
import { prettyDay, RangePicker, Tile, usd, useRange, type Range } from "./common";

interface Payment {
  id: string; day: string; paidAt: string; ticketNo: number; customer: string; state: string | null;
  kind: "charge" | "refund"; method: string; card: string | null; amount: number;
  /** Made the ticket paid in full: the ticket's tax and insurance are shown on this payment only (else 0). */
  completes: boolean; tax: number; insurance: number; by: string; reference: string | null;
}

const METHOD: Record<string, string> = { card: "Card", check: "Check", check_by_mail: "Check by mail", paypal: "PayPal", venmo: "Venmo", bank: "Bank", other: "Other" };
const method = (m: string) => METHOD[m] ?? m;

function downloadCsv(name: string, rows: (string | number | null)[][]) {
  const csv = rows.map((r) => r.map((v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; }).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = name;
  a.click();
}

/**
 * Payments (owner, 2026-11-04; old Payments page): every card, check or other payment and refund in the range by
 * payment date (Central). Transactions is a grid to sort, filter and export; Daily report groups them by day with
 * totals by method (the old closing totals) and a range total. A ticket's sales tax and insurance show once, on the
 * payment that made it paid in full (owner-approved, 2026-11-04), so the tax and insurance totals equal the Dashboard's.
 */
export function PaymentsPage({ config }: { config: PortalConfig }) {
  const [range, setRange] = useRange();
  const [view, setView] = useState<"list" | "daily">("list");
  const { data, error, loading } = useApi<{ range: Range; rows: Payment[] }>(config, `/reports/payments?from=${range.from}&to=${range.to}`);
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const grid = useRef<GridApi<Payment> | null>(null);

  const sum = (f: (p: Payment) => number, only?: (p: Payment) => boolean) => rows.filter(only ?? (() => true)).reduce((s, p) => s + f(p), 0);
  const net = sum((p) => p.amount);
  const charges = sum((p) => p.amount, (p) => p.kind === "charge");
  const refunds = Math.abs(sum((p) => p.amount, (p) => p.kind === "refund"));

  const columns = useMemo<ColDef<Payment>[]>(() => [
    { field: "paidAt", headerName: "Date", width: 165, sort: "desc", filter: false, valueFormatter: (p: ValueFormatterParams<Payment, string>) => dateTime(p.value) },
    { field: "ticketNo", headerName: "Ticket", width: 100, filter: "agNumberColumnFilter",
      cellRenderer: (p: ICellRendererParams<Payment>) => p.data ? <Link to={`/tickets/${p.data.ticketNo}`}>{p.data.ticketNo}</Link> : null },
    { field: "customer", headerName: "Customer", width: 170 },
    { field: "kind", headerName: "Type", width: 100, valueFormatter: (p) => (p.value === "refund" ? "Refund" : "Payment") },
    { field: "method", headerName: "Method", width: 120, valueFormatter: (p) => method(String(p.value)) },
    { field: "card", headerName: "Card", width: 150 },
    { field: "amount", headerName: "Amount", width: 120, type: "rightAligned", filter: "agNumberColumnFilter", valueFormatter: (p) => usd(Number(p.value)),
      cellClass: (p) => (Number(p.value) < 0 ? "neg" : "") },
    { field: "tax", headerName: "Sales tax", headerTooltip: "The ticket's sales tax, on the payment that paid it in full", width: 110, type: "rightAligned", filter: "agNumberColumnFilter", valueFormatter: (p) => (p.value ? usd(Number(p.value)) : "—") },
    { field: "insurance", headerName: "Insurance", headerTooltip: "The ticket's insurance, on the payment that paid it in full", width: 110, type: "rightAligned", filter: "agNumberColumnFilter", valueFormatter: (p) => (p.value ? usd(Number(p.value)) : "—") },
    { field: "state", headerName: "Ship-to state", width: 120 },
    { field: "by", headerName: "Taken by", width: 190 },
    { field: "reference", headerName: "Transaction", width: 150, hide: true },
  ], []);

  const days = useMemo(() => {
    const map = new Map<string, Payment[]>();
    for (const p of [...rows].sort((a, b) => a.paidAt.localeCompare(b.paidAt))) map.set(p.day, [...(map.get(p.day) ?? []), p]);
    return [...map.entries()].reverse();
  }, [rows]);
  const byMethod = (list: Payment[]) => Object.entries(list.reduce<Record<string, number>>((acc, p) => ({ ...acc, [p.method]: (acc[p.method] ?? 0) + p.amount }), {}));

  return (
    <div className="report-page">
      <div className="report-head"><h2>Payments</h2>
        <div className="seg-toggle" role="group" aria-label="View">
          <button type="button" className={view === "list" ? "on" : ""} aria-pressed={view === "list"} onClick={() => setView("list")}>Transactions</button>
          <button type="button" className={view === "daily" ? "on" : ""} aria-pressed={view === "daily"} onClick={() => setView("daily")}>Daily report</button>
        </div>
      </div>
      <RangePicker range={range} onChange={setRange}>
        <button type="button" className="tb-btn" disabled={!rows.length} onClick={() => view === "list"
          ? grid.current?.exportDataAsCsv({ fileName: `payments-${range.from}-to-${range.to}.csv` })
          : downloadCsv(`daily-payments-${range.from}-to-${range.to}.csv`, [["Day", "Method", "Amount"], ...days.flatMap(([d, list]) => byMethod(list).map(([m, a]) => [d, method(m), a.toFixed(2)]))])}>
          <Download size={15} />CSV</button>
      </RangePicker>
      {error && <p className="error">{error.message}</p>}
      <div className="tiles">
        <Tile label="Net sales (incl. tax)" value={net} money />
        <Tile label="Payments" value={charges} money />
        <Tile label="Refunds" value={refunds} money />
        <Tile label="Sales tax" value={sum((p) => p.tax)} money hint="Tax of the tickets paid in full in the range" />
        <Tile label="Insurance" value={sum((p) => p.insurance)} money hint="Insurance of the tickets paid in full in the range" />
        <Tile label="Transactions" value={rows.length} />
      </div>
      {loading && <p className="muted">Loading…</p>}
      {view === "list" ? (
        <div className="grid-box report-grid">
          <AgGridReact<Payment> theme={theme} rowData={data ? rows : null} columnDefs={columns} getRowId={(p) => p.data.id}
            defaultColDef={{ sortable: true, filter: "agTextColumnFilter", resizable: true }} onGridReady={(e) => { grid.current = e.api; }}
            pagination paginationPageSize={100} overlayNoRowsTemplate="No payments in this range." loading={!data && !error} />
        </div>
      ) : (
        <div className="daily">
          {!days.length && data && <p className="muted">No payments in this range.</p>}
          {days.map(([d, list]) => (
            <section key={d} className="day-card">
              <header><h3>{prettyDay(d)}</h3><span className="day-total">{usd(list.reduce((s, p) => s + p.amount, 0))}</span></header>
              <p className="day-methods">{byMethod(list).map(([m, a]) => <span key={m}>{method(m)} <strong>{usd(a)}</strong></span>)}
                <span>Tax <strong>{usd(list.reduce((s, p) => s + p.tax, 0))}</strong></span><span>Insurance <strong>{usd(list.reduce((s, p) => s + p.insurance, 0))}</strong></span></p>
              <table className="t-plain">
                <thead><tr><th>Time</th><th>Ticket</th><th>Customer</th><th>Type</th><th>Method</th><th className="num">Amount</th><th className="num">Tax</th><th className="num">Insurance</th><th>State</th></tr></thead>
                <tbody>{list.map((p) => (
                  <tr key={p.id}><td>{new Date(p.paidAt).toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" })}</td>
                    <td><Link to={`/tickets/${p.ticketNo}`}>{p.ticketNo}</Link></td><td>{p.customer}</td><td>{p.kind === "refund" ? "Refund" : "Payment"}</td>
                    <td>{method(p.method)}{p.card ? ` · ${p.card}` : ""}</td><td className={`num${p.amount < 0 ? " neg" : ""}`}>{usd(p.amount)}</td>
                    <td className="num">{p.tax ? usd(p.tax) : "—"}</td><td className="num">{p.insurance ? usd(p.insurance) : "—"}</td><td>{p.state ?? ""}</td></tr>
                ))}</tbody>
              </table>
            </section>
          ))}
          {days.length > 0 && <p className="range-total">Range total ({prettyDay(range.from)} – {prettyDay(range.to)}): <strong>{usd(net)}</strong>
            {byMethod(rows).map(([m, a]) => <span key={m}> · {method(m)} {usd(a)}</span>)}</p>}
        </div>
      )}
    </div>
  );
}
